/**
 * Order line items — a compact card per item with thumbnail, product info,
 * per-item chips (condition, size/color, SKU, serial, surcharge, return
 * status), pricing, and refund status. Parity with reware-admin's order-item
 * block. Includes an all/active/returned/removed status filter.
 */
import { useState } from 'react';
import { Avatar, Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CheckroomIcon from '@mui/icons-material/Checkroom';

import { displayNameFor, SectionLabel, semanticStateColor } from '@src/components/indexPage';

import { formatMoney } from './format';
import { present } from './types';
import type { ConstantOption, OrderItem, OrderRecord } from './types';

type ItemFilter = 'all' | 'active' | 'returned' | 'removed';

const itemMatchesFilter = (item: OrderItem, filter: ItemFilter): boolean => {
  switch (filter) {
    case 'active':
      return !item.removedAt && !item.returnedAt;
    case 'returned':
      return !!item.returnedAt || !!item.returnInitiatedAt;
    case 'removed':
      return !!item.removedAt;
    default:
      return true;
  }
};

const ItemChip = ({ label, value }: { label: string, value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={(
        <span>
          {label}
          {': '}
          <b>{value}</b>
        </span>
      )}
    />
  );
};

const OrderItemCard = ({
  item,
  currency,
  conditions,
}: {
  item: OrderItem;
  currency: string | null | undefined;
  conditions: ConstantOption[];
}) => {
  const catalog = item.catalogItem;
  const inv = item.inventoryItem;
  const image = inv?.imageUrls ? (inv.imageUrls.front ?? Object.values(inv.imageUrls)[0]) : undefined;
  const statusLabel = item.removedAt ? 'Removed' : item.returnedAt ? 'Returned' : item.returnInitiatedAt ? 'Return in progress' : inv?.state;
  const statusColor = item.removedAt || item.returnedAt ? 'error' : semanticStateColor(inv?.state ?? undefined);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        opacity: item.removedAt ? 0.6 : 1,
      }}
    >
      <Avatar
        variant="rounded"
        src={image}
        alt={catalog?.title ?? 'Item'}
        sx={{ width: 56, height: 56, bgcolor: 'grey.100', color: 'grey.500', border: '1px solid', borderColor: 'divider' }}
      >
        <CheckroomIcon fontSize="small" />
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {catalog?.title ?? `Item ${item.id}`}
          </Typography>
          {statusLabel && <Chip size="small" color={statusColor} label={statusLabel} />}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {`Item #${inv?.id ?? item.id} · ${catalog?.sku ?? 'No SKU'}`}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
          <ItemChip label="Condition" value={displayNameFor(conditions, item.sale_condition)} />
          <ItemChip label="Size" value={catalog?.size} />
          <ItemChip
            label="Color"
            value={catalog?.color ? `${catalog.color}${catalog.display_color && catalog.display_color !== catalog.color ? ` (${catalog.display_color})` : ''}` : null}
          />
          <ItemChip label="Serial" value={inv?.troveSerialNumber} />
          <ItemChip label="Location" value={inv?.location?.code} />
          {item.externalReturnId && <ItemChip label="Return" value={item.externalReturnId} />}
        </Stack>
        {item.saleNotes && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
            {item.saleNotes}
          </Typography>
        )}
        {item.refund && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main' }}>
            {`Refund ${item.refund.state ?? ''}`}
            {item.refund.flaggedReason ? ` — ${item.refund.flaggedReason}` : ''}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', minWidth: 90 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(item.saleDisplayPrice ?? item.sale_price, currency)}
        </Typography>
        {!!item.sale_surcharge_amount && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {`+${formatMoney(item.sale_surcharge_amount, currency)} surcharge`}
          </Typography>
        )}
        {!!item.refundValue && (
          <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
            {`(${formatMoney(item.refundValue, currency)} refunded)`}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export const OrderItems = ({ order, conditions }: { order: OrderRecord, conditions: ConstantOption[] }) => {
  const [filter, setFilter] = useState<ItemFilter>('all');
  const items = present(order.orderItems);
  const visible = items.filter(item => itemMatchesFilter(item, filter));

  if (!items.length) return null;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <SectionLabel>{`Items (${items.length})`}</SectionLabel>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_event, value: ItemFilter | null) => value && setFilter(value)}
          sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: 11.5, textTransform: 'none' } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="returned">Returned</ToggleButton>
          <ToggleButton value="removed">Removed</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack spacing={1}>
        {visible.map(item => (
          <OrderItemCard key={item.id} item={item} currency={order.transactionCurrencyCode} conditions={conditions} />
        ))}
        {!visible.length && (
          <Typography variant="body2" sx={{ color: 'text.disabled', py: 1 }}>
            No items match this filter.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default OrderItems;
